import React, { useEffect, useLayoutEffect, useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { commercialConfigAPI } from '../services/api';
import { loadReportProfiles } from '../utils/reportProfiles';
import AIChatWidget from './AIChatWidget';
import StickyHScrollbar from './StickyHScrollbar';
import useDraggablePosition from '../hooks/useDraggablePosition';
import { useAppSettings } from '../context/AppSettingsContext';
import {
  FaHome,
  FaCapsules,
  FaFlask,
  FaTools,
  FaFileAlt,
  FaFolderOpen,
  FaMicroscope,
  FaEye,
  FaCalculator,
  FaClipboardList,
  FaExclamationTriangle,
  FaCheckCircle,
  FaGraduationCap,
  FaChartLine,
  FaRedoAlt,
  FaHistory,
  FaClipboardCheck,
  FaUsers,
  FaSignOutAlt,
  FaBuilding,
  FaPlug,
  FaBars,
  FaCog,
  FaOilCan,
  FaPrescriptionBottleAlt,
  FaSun,
  FaMoon,
  FaChartBar,
  FaShieldAlt,
  FaRobot,
  FaChevronDown,
  FaTimes,
  FaUserCircle,
  FaArrowLeft,
  FaEllipsisH,
  FaPalette
} from 'react-icons/fa';
import { FaLayerGroup } from 'react-icons/fa6';
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from 'react-icons/ai';
import Breadcrumbs from './Breadcrumbs';
import { publishModuleAccess, MODULE_ACCESS_CHANGED } from '../utils/moduleAccess';
import { useTheme, colorOptions } from '../context/ThemeContext';

const SIDEBAR_COMPACT_BREAKPOINT = 1024;
// Below this the header's horizontal-scroll row runs out of room for the
// unit/plant chip alongside the section chip + theme toggle + avatar, so it
// moves back into the sidebar instead of forcing the header to scroll.
const UNIT_CHIP_MOBILE_BREAKPOINT = 400;
const ROUTE_LABELS = { oral: 'Oral', topical: 'Topical', injectable: 'Injectable', inhalation: 'Inhalation', ophthalmic: 'Ophthalmic', transdermal: 'Transdermal' };

// Renders its children into document.body, positioned by the anchor's live
// bounding box. Needed because the header/sidebar scroll containers clip any
// position:absolute menu that would otherwise render inside them — a portal
// escapes that clipping instead of fighting it with z-index.
const DropdownPortal = ({ open, anchorRef, align = 'right', placement = 'below', onClose, children }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      // 'above' is for anchors pinned to the bottom of the viewport (the
      // tablet bottom nav) — opening downward there would render off-screen.
      ...(placement === 'above'
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
      left: align === 'right' ? undefined : rect.left,
      right: align === 'right' ? window.innerWidth - rect.right : undefined,
      width: align === 'stretch' ? rect.width : undefined
    });
  }, [anchorRef, align, placement]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    const handleReposition = () => updatePos();
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, anchorRef, onClose, updatePos]);

  if (!open || !pos) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right, width: pos.width, zIndex: 1400 }}
    >
      {children}
    </div>,
    document.body
  );
};

const HeaderDropdown = ({ label, value, options, onChange, variant, showLabel = true, displayLabel, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const isLight = variant === 'light';

  const current = options.find(o => o.value === value) || options[0];

  return (
    <div className="header-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className={`header-dropdown-trigger${isLight ? ' header-dropdown-trigger--light' : ''}`}
        onClick={() => setOpen(p => !p)}
      >
        {showLabel && <span style={{ fontWeight: 500, opacity: 0.8, whiteSpace: 'nowrap', flexShrink: 0 }}>{label}:</span>}
        <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{displayLabel ?? current?.label}</span>
        <span style={{ fontSize: 'var(--fs-2xs)', opacity: 0.75, marginLeft: '2px', flexShrink: 0, whiteSpace: 'nowrap' }}>{open ? '▲' : '▼'}</span>
      </button>
      <DropdownPortal open={open} anchorRef={triggerRef} align={align} onClose={() => setOpen(false)}>
        <div className="header-dropdown-menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`header-dropdown-item${opt.value === value ? ' active' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </DropdownPortal>
    </div>
  );
};

// Only these top-level entries get a permanent slot in the bottom bar —
// everything else (Risk Assessment, AI Assistant, Cleaning Config, Analytical
// Methods, VRL, Training, CCV, Revalidation, Audit Trail, Settings, ...)
// lives behind "More" so the bar stays a handful of buttons instead of the
// full ~13-item sidebar squeezed into one row.
const TABLET_MAIN_NAV_KEYS = ['/dashboard', 'master', 'reports', '/results'];

// Fixed bottom tab bar shown on tablet/mobile — an alternative to opening
// the off-canvas sidebar for the common case of jumping straight to a page.
// Groups (Master, Reports) and "More" open a dropdown of their children
// upward (placement="above"), since their anchors sit at the screen's
// bottom edge.
const TabletBottomNav = ({ items, getVisibleChildren, location, onNavigate }) => {
  const [openKey, setOpenKey] = useState(null);
  const anchorRefs = useRef({});

  const getAnchorRef = (key) => {
    if (!anchorRefs.current[key]) anchorRefs.current[key] = { current: null };
    return anchorRefs.current[key];
  };

  const itemKey = (item) => item.path || item.groupKey;
  const isGroupActive = (item) => Array.isArray(item.children) && item.children.some((c) => location.pathname === c.path);

  const mainItems = items.filter((item) => TABLET_MAIN_NAV_KEYS.includes(itemKey(item)));
  const moreItems = items.filter((item) => !TABLET_MAIN_NAV_KEYS.includes(itemKey(item)));
  const isMoreActive = moreItems.some((item) => (item.path ? location.pathname === item.path : isGroupActive(item)));

  return (
    <nav className="tablet-bottom-nav" aria-label="Quick navigation">
      {mainItems.map((item) => {
        const key = itemKey(item);

        if (Array.isArray(item.children)) {
          const anchorRef = getAnchorRef(key);
          const visibleChildren = getVisibleChildren(item.children);
          const active = isGroupActive(item);
          const isOpen = openKey === key;
          return (
            <div className="tablet-bottom-nav-item" key={key}>
              <button
                type="button"
                ref={(el) => { anchorRef.current = el; }}
                className={`tablet-bottom-nav-btn ${active ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                onClick={() => setOpenKey((p) => (p === key ? null : key))}
                title={item.label}
              >
                <span className="tablet-bottom-nav-icon">{item.icon}</span>
                <span className="tablet-bottom-nav-label">{item.label}</span>
              </button>
              <DropdownPortal open={isOpen} anchorRef={anchorRef} align="left" placement="above" onClose={() => setOpenKey(null)}>
                <div className="header-dropdown-menu">
                  {visibleChildren.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`header-dropdown-item ${location.pathname === child.path ? 'active' : ''}`}
                      onClick={() => { setOpenKey(null); onNavigate(); }}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </DropdownPortal>
            </div>
          );
        }

        const active = location.pathname === item.path;
        return (
          <Link
            key={key}
            to={item.path}
            className={`tablet-bottom-nav-btn ${active ? 'active' : ''}`}
            onClick={onNavigate}
            title={item.label}
          >
            <span className="tablet-bottom-nav-icon">{item.icon}</span>
            <span className="tablet-bottom-nav-label">{item.label}</span>
          </Link>
        );
      })}

      {moreItems.length > 0 && (() => {
        const anchorRef = getAnchorRef('more');
        const isOpen = openKey === 'more';
        return (
          <div className="tablet-bottom-nav-item" key="more">
            <button
              type="button"
              ref={(el) => { anchorRef.current = el; }}
              className={`tablet-bottom-nav-btn ${isMoreActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
              onClick={() => setOpenKey((p) => (p === 'more' ? null : 'more'))}
              title="More"
            >
              <span className="tablet-bottom-nav-icon"><FaEllipsisH /></span>
              <span className="tablet-bottom-nav-label">More</span>
            </button>
            <DropdownPortal open={isOpen} anchorRef={anchorRef} align="right" placement="above" onClose={() => setOpenKey(null)}>
              <div className="header-dropdown-menu tablet-more-menu">
                {moreItems.map((item) => {
                  if (Array.isArray(item.children)) {
                    const visibleChildren = getVisibleChildren(item.children);
                    return (
                      <div className="tablet-more-group" key={item.groupKey}>
                        <div className="tablet-more-group-label">{item.label}</div>
                        {visibleChildren.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`header-dropdown-item ${location.pathname === child.path ? 'active' : ''}`}
                            onClick={() => { setOpenKey(null); onNavigate(); }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`header-dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={() => { setOpenKey(null); onNavigate(); }}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </DropdownPortal>
          </div>
        );
      })()}
    </nav>
  );
};

const Layout = ({ user, onLogout }) => {
  const { appName, clientName, logoUrl } = useAppSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const floatingBack = useDraggablePosition({
    storageKey: 'floating-back-button-position',
    size: 44,
    side: 'left',
    bottomOffset: 24 + (window.innerWidth <= SIDEBAR_COMPACT_BREAKPOINT ? 60 : 0)
  });
  const [moduleAccess, setModuleAccess] = useState(null);
  // Accordion — only one sidebar group open at a time, like the LMS sidebar.
  const [expandedGroup, setExpandedGroup] = useState('master');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(() => window.innerWidth <= SIDEBAR_COMPACT_BREAKPOINT);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const colorMenuRef = useRef(null);
  const headerRef = useRef(null);
  const [isNarrowMobile, setIsNarrowMobile] = useState(() => window.innerWidth <= UNIT_CHIP_MOBILE_BREAKPOINT);
  const [activePlant, setActivePlant] = useState(() => localStorage.getItem('activePlant') || '');
  const [availablePlants, setAvailablePlants] = useState(() => {
    try {
      const cachedPlants = JSON.parse(localStorage.getItem('availablePlants') || '[]');
      return Array.isArray(cachedPlants) ? cachedPlants : [];
    } catch {
      return [];
    }
  });
  const [plantContextReady, setPlantContextReady] = useState(() => {
    try {
      const cachedPlants = JSON.parse(localStorage.getItem('availablePlants') || '[]');
      return (Array.isArray(cachedPlants) && cachedPlants.length > 0) || localStorage.getItem('activePlant') === 'All Units';
    } catch {
      return localStorage.getItem('activePlant') === 'All Units';
    }
  });
  const [plantName, setPlantName] = useState(() => {
    const storedCode = localStorage.getItem('activePlant') || '';
    if (storedCode === 'All Units') return 'All Units';
    try {
      const cachedPlants = JSON.parse(localStorage.getItem('availablePlants') || '[]');
      const selected = Array.isArray(cachedPlants)
        ? cachedPlants.find((plant) => String(plant.plant_code).toLowerCase() === storedCode.toLowerCase())
        : null;
      return selected?.plant_name || '';
    } catch {
      return '';
    }
  });
  const [activeLine, setActiveLine] = useState(() => localStorage.getItem('activeLine') || '');
  const [availableLines, setAvailableLines] = useState([]);
  const isSuperAdmin = String(user?.role || '').trim().toLowerCase() === 'super admin';
  const roleKey = String(user?.role || '').trim().toLowerCase();
  const isCrossUnitViewer = ['super admin', 'admin'].includes(roleKey) || /corporate/i.test(String(user?.department || '')) || /corporate/i.test(roleKey);
  const { theme, toggleTheme, colorTheme, setColorTheme } = useTheme();

  // Report header/footer profiles are read synchronously by the print/PDF code.
  useEffect(() => { loadReportProfiles(); }, []);

  useEffect(() => {
    let isMounted = true;
    const ROUTE_LABELS_INIT = { oral: 'Oral', topical: 'Topical', injectable: 'Injectable', inhalation: 'Inhalation', ophthalmic: 'Ophthalmic', transdermal: 'Transdermal' };
    const initLineRoute = (lines) => {
      const stored = localStorage.getItem('activeLine') || '';
      if (!stored || stored === 'All') return;
      const selected = lines.find(l => l.line_name === stored);
      if (selected) localStorage.setItem('activeLineId', String(selected.line_id));
      if (localStorage.getItem('activeLineRoute')) return;
      const match = selected;
      if (match?.route_of_admin) {
        localStorage.setItem('activeLineRoute', match.route_of_admin);
        localStorage.setItem('activeLineDosageLabel', ROUTE_LABELS_INIT[match.route_of_admin] || match.route_of_admin);
      }
    };
    const loadModuleAccess = async () => {
      try {
        const response = await commercialConfigAPI.getRuntime();
        const data = response?.data?.data;
        const modules = data?.modules;
        if (isMounted && modules && typeof modules === 'object') {
          setModuleAccess(modules);
          publishModuleAccess(modules);
        }
        if (isMounted && data?.plants) {
          localStorage.setItem('availablePlants', JSON.stringify(data.plants));
          const currentCode = localStorage.getItem('activePlant');
          const p = data.plants.find(plant => String(plant.plant_code).toLowerCase() === String(currentCode).toLowerCase());
          if (p) {
            localStorage.setItem('activePlant', p.plant_code);
            localStorage.setItem('activePlantId', String(p.plant_id));
            setActivePlant(p.plant_code);
          }
          if (p) {
            setPlantName(p.plant_name);
          } else if (currentCode === 'All Units' && isCrossUnitViewer) {
            setPlantName('All Units');
          } else if ((!currentCode || currentCode === 'All' || !data.plants.some(plant => String(plant.plant_code).toLowerCase() === String(currentCode).toLowerCase())) && isCrossUnitViewer && data.plants.length > 1) {
            localStorage.setItem('activePlant', 'All Units');
            localStorage.removeItem('activePlantId');
            setActivePlant('All Units');
            setPlantName('All Units');
            window.dispatchEvent(new Event('site-context-changed'));
          } else if ((!currentCode || currentCode === 'All' || !data.plants.some(plant => String(plant.plant_code).toLowerCase() === String(currentCode).toLowerCase())) && data.plants.length === 1) {
            // Single-plant tenants never see the header plant switcher (it only
            // renders when there's a choice to make), so seed activePlant here —
            // otherwise plant_code-scoped saves/filters would stay blank forever.
            const only = data.plants[0];
            localStorage.setItem('activePlant', only.plant_code);
            localStorage.setItem('activePlantId', String(only.plant_id));
            setActivePlant(only.plant_code);
            setPlantName(only.plant_name);
            window.dispatchEvent(new Event('site-context-changed'));
          } else if (!p && data.plants.length > 0) {
            // Prefer the user's own assigned unit (set by an Admin in User
            // Management) so a non-Admin lands on their own data instead of
            // whichever plant happens to be first — previously this always
            // picked data.plants[0], which could be a unit the user has no
            // records in at all, making their lists look empty.
            const userDefault = data.plants.find(plant => String(plant.plant_code).toLowerCase() === String(user?.default_plant_code || '').toLowerCase());
            const chosen = userDefault || data.plants[0];
            localStorage.setItem('activePlant', chosen.plant_code);
            localStorage.setItem('activePlantId', String(chosen.plant_id));
            setActivePlant(chosen.plant_code);
            setPlantName(chosen.plant_name);
            if (userDefault && user?.default_line_name && !localStorage.getItem('activeLine')) {
              localStorage.setItem('activeLine', user.default_line_name);
              setActiveLine(user.default_line_name);
            }
            window.dispatchEvent(new Event('site-context-changed'));
          }
          setAvailablePlants(data.plants);
        }
        if (isMounted && data?.lines) {
          setAvailableLines(data.lines || []);
          initLineRoute(data.lines || []);
        } else {
          // fallback to getFull if runtime doesn't include lines
          const fullRes = await commercialConfigAPI.getFull();
          const fallbackLines = fullRes?.data?.data?.lines || [];
          if (isMounted) setAvailableLines(fallbackLines);
          initLineRoute(fallbackLines);
        }
      } catch (error) {
        // Keep menu visible if runtime configuration is unavailable
    } finally {
      if (isMounted) setPlantContextReady(true);
      }
    };

    loadModuleAccess();
    return () => {
      isMounted = false;
    };
  }, [isCrossUnitViewer]);

  useEffect(() => {
    const handleResize = () => {
      const compact = window.innerWidth <= SIDEBAR_COMPACT_BREAKPOINT;
      setIsTabletOrMobile(compact);
      setIsNarrowMobile(window.innerWidth <= UNIT_CHIP_MOBILE_BREAKPOINT);
      if (compact) {
        setSidebarCollapsed(false);
      } else {
        setSidebarMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Exposed so any fixed-position element (floating back button, AI chat FAB)
  // can push itself up above the bottom tab bar instead of being covered by
  // it — see .tablet-bottom-nav in App.css. Kept as a CSS var rather than a
  // hardcoded offset in each of those components so there's one source of
  // truth for the bar's height.
  useEffect(() => {
    document.documentElement.style.setProperty('--tablet-bottom-nav-height', isTabletOrMobile ? '60px' : '0px');
  }, [isTabletOrMobile]);

  // The mobile/tablet sidebar overlay and its backdrop are fixed-positioned
  // below the header, so they need to know its real rendered height (which
  // varies by breakpoint and by how many chips fit in one row) rather than a
  // guessed constant — otherwise a gap (or an overlap) opens up between the
  // header and the drawer. Measure it live and expose it as a CSS variable.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const setHeaderHeightVar = () => {
      document.documentElement.style.setProperty('--header-height', `${el.offsetHeight}px`);
    };
    setHeaderHeightVar();
    const observer = new ResizeObserver(setHeaderHeightVar);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const syncContext = () => {
      const code = localStorage.getItem('activePlant') || '';
      setActivePlant(code);
      setActiveLine(localStorage.getItem('activeLine') || '');
    };

    window.addEventListener('site-context-changed', syncContext);
    return () => window.removeEventListener('site-context-changed', syncContext);
  }, []);

  const filteredLines = useMemo(() => {
    return availableLines.filter(l => {
      if (!activePlant || activePlant === 'All' || activePlant === 'All Units') return true;
      const selectedPlant = availablePlants.find((plant) => String(plant.plant_code).toLowerCase() === String(activePlant).toLowerCase());
      const linePlant = String(l.plant_code || '').trim().toLowerCase();
      return !!linePlant && [String(activePlant).trim().toLowerCase(), String(selectedPlant?.plant_name || '').trim().toLowerCase()].includes(linePlant);
    });
  }, [availableLines, availablePlants, activePlant]);

  useEffect(() => {
    if (!availableLines.length) return;
    if (!activeLine || activeLine === 'All' || filteredLines.some((line) => line.line_name === activeLine)) return;
    localStorage.setItem('activeLine', 'All');
    localStorage.removeItem('activeLineRoute');
    localStorage.removeItem('activeLineDosageLabel');
    setActiveLine('All');
  }, [activeLine, availableLines, filteredLines]);

  const lineOptions = useMemo(() => ([
    { value: 'All', label: 'All Sections' },
    ...filteredLines.map(l => ({ value: l.line_name, label: l.line_name + (l.route_of_admin ? ` (${ROUTE_LABELS[l.route_of_admin] || l.route_of_admin})` : '') }))
  ]), [filteredLines]);

  const handlePlantChange = (val) => {
    setActivePlant(val);
    localStorage.setItem('activePlant', val);
    const selectedPlant = availablePlants.find(p => p.plant_code === val);
    if (selectedPlant) localStorage.setItem('activePlantId', String(selectedPlant.plant_id));
    else localStorage.removeItem('activePlantId');
    localStorage.setItem('activeLine', 'All');
    setActiveLine('All');
    const p = selectedPlant;
    setPlantName(p ? p.plant_name : val === 'All Units' ? 'All Units' : '');
    window.dispatchEvent(new Event('site-context-changed'));
    window.location.reload();
  };

  const handleLineChange = (val) => {
    setActiveLine(val);
    localStorage.setItem('activeLine', val);
    const lineObj = filteredLines.find(l => l.line_name === val);
    if (lineObj) localStorage.setItem('activeLineId', String(lineObj.line_id));
    else localStorage.removeItem('activeLineId');
    localStorage.setItem('activeLineRoute', lineObj?.route_of_admin || '');
    localStorage.setItem('activeLineDosageLabel', lineObj ? (ROUTE_LABELS[lineObj.route_of_admin] || lineObj.route_of_admin || '') : '');
    window.dispatchEvent(new Event('site-context-changed'));
    window.location.reload();
  };

  const menuItems = useMemo(
    () => [
      { path: '/dashboard', icon: <FaHome />, label: 'Dashboard', moduleKey: 'dashboard' },
      {
        groupKey: 'master',
        icon: <FaLayerGroup />,
        label: 'Master',
        children: [
          { path: '/api-master', icon: <FaFlask />, label: 'API Master', moduleKey: 'product_master' },
          { path: '/product-master', icon: <FaCapsules />, label: 'Product Master', moduleKey: 'product_master' },
          { path: '/equipment-master', icon: <FaPrescriptionBottleAlt />, label: 'Equipment Master', moduleKey: 'equipment_master' },
          { path: '/equipment-trains', icon: <FaOilCan />, label: 'Equipment Trains', moduleKey: 'equipment_master' },
          { path: '/users', icon: <FaUsers />, label: 'User Management', moduleKey: 'user_management' }
        ]
      },
      {
        groupKey: 'reports',
        icon: <FaChartBar />,
        label: 'Reports',
        children: [
          { path: '/worst-case', icon: <FaCalculator />, label: 'Worst Case', moduleKey: 'worst_case' },
          { path: '/maco', icon: <FaCalculator />, label: 'MACO Calculation', moduleKey: 'maco' },
          { path: '/acceptance-criteria', icon: <FaCheckCircle />, label: 'Sampling Plan & Acceptance Criteria', moduleKey: ['sampling_plans', 'acceptance_criteria'] },
          { path: '/study-report', icon: <FaClipboardList />, label: 'Study Report', moduleKey: 'sampling_plans' }
        ]
      },
      {
        groupKey: 'assessments',
        icon: <FaShieldAlt />,
        label: 'Assessments',
        children: [
          { path: '/risk-assessment', icon: <FaShieldAlt />, label: 'Risk Assessment', moduleKey: 'risk_assessment' },
          { path: '/gap-assessment', icon: <FaExclamationTriangle />, label: 'Gap Assessment', moduleKey: 'sampling_plans' },
          { path: '/cleaning-validation-settings', icon: <FaFileAlt />, label: 'CV Coverage' }
        ]
      },
      { path: '/ai-assistant', icon: <FaRobot />, label: 'AI Assistant', moduleKey: 'ai_assistant' },
      { path: '/cleaning-config', icon: <FaFileAlt />, label: 'Cleaning Config', moduleKey: 'cleaning_config' },
      { path: '/analytical-methods', icon: <FaMicroscope />, label: 'Analytical Methods', moduleKey: 'analytical_methods' },
      { path: '/vrl', icon: <FaEye />, label: 'VRL Studies', moduleKey: 'vrl' },
      { path: '/results', icon: <FaCheckCircle />, label: 'Results', moduleKey: 'results' },
      { path: '/training', icon: <FaGraduationCap />, label: 'Training', moduleKey: 'training' },
      { path: '/documents', icon: <FaFolderOpen />, label: 'Documents', moduleKey: 'documents' },
      { path: '/ccv', icon: <FaChartLine />, label: 'CCV', moduleKey: 'ccv' },
      { path: '/revalidation', icon: <FaRedoAlt />, label: 'Revalidation', moduleKey: 'revalidation' },
      { path: '/audit-trail', icon: <FaHistory />, label: 'Audit Trail', moduleKey: 'audit_trail' },
      { path: '/change-control', icon: <FaClipboardCheck />, label: 'Change Control', moduleKey: 'change_control' },
      ...(isSuperAdmin ? [{
        groupKey: 'settings',
        icon: <FaTools />,
        label: 'Settings',
        children: [
          { path: '/settings', icon: <FaCog />, label: 'App Configuration' },
          { path: '/integrations', icon: <FaPlug />, label: 'Integration Hub' },
          { path: '/commercial-setup', icon: <FaBuilding />, label: 'Commercial Setup' }
        ]
      }] : [])
    ],
    [isSuperAdmin]
  );

  // Commercial Setup announces a saved plan, App Configuration saved units /
  // sections; reload which modules are on and the unit / section selectors.
  useEffect(() => {
    const reload = async () => {
      try {
        const response = await commercialConfigAPI.getRuntime();
        const data = response?.data?.data;
        const modules = data?.modules;
        if (modules && typeof modules === 'object') {
          setModuleAccess(modules);
          publishModuleAccess(modules);
        }
        if (Array.isArray(data?.plants)) {
          localStorage.setItem('availablePlants', JSON.stringify(data.plants));
          setAvailablePlants(data.plants);
          setAvailableLines(data.lines || []);
        }
      } catch (error) {
        // keep the list already loaded
      }
    };
    window.addEventListener(MODULE_ACCESS_CHANGED, reload);
    return () => window.removeEventListener(MODULE_ACCESS_CHANGED, reload);
  }, []);

  // A menu entry may belong to more than one module; it shows while any of them is on.
  const anyModuleOn = (moduleKey) => [].concat(moduleKey).some((key) => moduleAccess[key] !== false);

  const filteredMenuItems = useMemo(() => {
    const isVisible = (item) => {
      if (item.alwaysVisibleForSuperAdmin && isSuperAdmin) return true;
      if (!item.moduleKey) return true;
      if (!moduleAccess || typeof moduleAccess !== 'object') return true;
      return [].concat(item.moduleKey).some((key) => moduleAccess[key] !== false);
    };

    return menuItems.filter((item) => {
      if (Array.isArray(item.children)) {
        const visibleChildren = item.children.filter(isVisible);
        return visibleChildren.length > 0;
      }
      return isVisible(item);
    });
  }, [menuItems, moduleAccess, isSuperAdmin]);

  const getVisibleChildren = (children = []) => {
    return children.filter((child) => {
      if (!child.moduleKey) return true;
      if (!moduleAccess || typeof moduleAccess !== 'object') return true;
      return anyModuleOn(child.moduleKey);
    });
  };

  // Opening a switched-off module by its address shows a notice instead of a page that cannot load.
  const currentNavItem = menuItems
    .flatMap((item) => (Array.isArray(item.children) ? item.children : [item]))
    .find((item) => item.path && (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)));
  const pageLocked = Boolean(
    currentNavItem?.moduleKey && moduleAccess && typeof moduleAccess === 'object'
    && !(currentNavItem.alwaysVisibleForSuperAdmin && isSuperAdmin)
    && !anyModuleOn(currentNavItem.moduleKey)
  );

  const toggleGroup = (groupKey) => {
    setExpandedGroup((prev) => (prev === groupKey ? null : groupKey));
  };

  const toggleSidebar = () => {
    if (isTabletOrMobile) {
      setSidebarMobileOpen((prev) => !prev);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  // Navigating to a top-level (non-group) item closes whichever group is open,
  // matching the LMS sidebar's accordion — only one thing expanded at a time.
  const handleNavClick = () => {
    if (isTabletOrMobile) setSidebarMobileOpen(false);
    setExpandedGroup(null);
  };

  // A sub-item's own group should stay open after navigating to it — only
  // close the mobile overlay, don't collapse the group the link lives in.
  const handleSubNavClick = () => {
    if (isTabletOrMobile) setSidebarMobileOpen(false);
  };

  return (
    <>
      <div className={`app layout-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${sidebarMobileOpen ? 'sidebar-open' : ''}`} style={{ display: 'flex' }}>
        {isTabletOrMobile && sidebarMobileOpen && <div className="sidebar-backdrop" onClick={() => setSidebarMobileOpen(false)} />}
        <aside className={`sidebar ${isTabletOrMobile ? 'tablet-sidebar' : ''}`}>
          <div className="sidebar-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="sidebar-brand-logo-img" />
            ) : (
              <span className="sidebar-brand-logo"><FaFlask /></span>
            )}
            <span className="sidebar-brand-text">
              <div className="sidebar-brand-name">{appName}</div>
              {clientName ? (
                <div className="sidebar-brand-client">{clientName}</div>
              ) : (
                <div className="sidebar-brand-tagline">Cleaning Validation Management</div>
              )}
            </span>
            {isTabletOrMobile && (
              <button
                type="button"
                className="sidebar-close"
                onClick={() => setSidebarMobileOpen(false)}
                aria-label="Close menu"
                title="Close menu"
              >
                <FaTimes />
              </button>
            )}
          </div>
          {isNarrowMobile && !sidebarCollapsed && plantContextReady && (availablePlants.length > 1 || plantName || activePlant) && (
            <div className="sidebar-unit-select">
              {availablePlants.length > 1 ? (
                <HeaderDropdown
                  showLabel={false}
                  label="Unit / Plant"
                  value={activePlant || ''}
                  options={availablePlants.map(p => ({ value: p.plant_code, label: p.plant_name || p.plant_code }))}
                  onChange={handlePlantChange}
                  align="stretch"
                />
              ) : (
                <div className="sidebar-unit-badge">{plantName || activePlant}</div>
              )}
            </div>
          )}
          <nav>
            <ul className="sidebar-nav">
              {filteredMenuItems.map((item) => (
                <li key={item.path || item.groupKey} className="sidebar-nav-item">
                  {Array.isArray(item.children) ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.groupKey)}
                        className={`sidebar-nav-link ${item.children.some((child) => location.pathname === child.path) ? 'group-active' : ''}`}
                        style={{ width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer' }}
                        title={sidebarCollapsed ? item.label : ''}
                      >
                        <span className="sidebar-icon-slot">
                          {item.icon}
                        </span>
                        {!sidebarCollapsed && <span className="sidebar-label" style={{ flex: 1 }}>{item.label}</span>}
                        {!sidebarCollapsed && (
                          <span
                            className="sidebar-chevron"
                            style={{ transform: expandedGroup === item.groupKey ? 'rotate(180deg)' : 'none' }}
                          >
                            <FaChevronDown />
                          </span>
                        )}
                      </button>
                      {expandedGroup === item.groupKey && (
                        <div style={{ marginLeft: sidebarCollapsed ? '0.1rem' : '1.25rem' }}>
                          {getVisibleChildren(item.children).map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={handleSubNavClick}
                              className={`sidebar-nav-link sidebar-sub-link ${location.pathname === child.path ? 'active' : ''}`}
                              style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                              title={sidebarCollapsed ? child.label : ''}
                            >
                              <span className="sidebar-icon-slot">
                                {child.icon || <span style={{ width: '1.2rem', display: 'inline-block' }} />}
                              </span>
                              {!sidebarCollapsed && <span className="sidebar-label">{child.label}</span>}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={handleNavClick}
                      className={`sidebar-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                      title={sidebarCollapsed ? item.label : ''}
                    >
                      <span className="sidebar-icon-slot">
                        {item.icon}
                      </span>
                      {!sidebarCollapsed && <span className="sidebar-label">{item.label}</span>}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          <div className="sidebar-user-card">
            <span className="sidebar-user-avatar">
              {(user?.username || '').slice(0, 2).toUpperCase()}
            </span>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.username}</div>
              <div className="sidebar-user-role">{user?.role}</div>
            </div>
          </div>
        </aside>
        <div className="main-column">
        <header className="header" ref={headerRef}>
        <div className="header-leading" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-outline sidebar-toggle"
            onClick={toggleSidebar}
            style={{ padding: '0.45rem 0.65rem' }}
            title={isTabletOrMobile ? 'Toggle sidebar' : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar (more space)')}
          >
            {!isTabletOrMobile && sidebarCollapsed ? <AiOutlineMenuUnfold /> : (!isTabletOrMobile ? <AiOutlineMenuFold /> : <FaBars />)}
          </button>
          <button
            type="button"
            className="btn btn-outline header-back-btn"
            onClick={() => navigate(-1)}
            style={{ padding: '0.45rem 0.65rem' }}
            title="Go back"
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>
          <span className="header-welcome">
            <span className="header-welcome-prefix">Welcome, </span>{user?.username}
          </span>
          {user?.role && <span className="header-role-badge">{user.role}</span>}
        </div>
          <div className="header-user" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div className="header-extras-panel">
            {/* Order: admin name (in header-leading) → unit/plant chip (hidden on narrow mobile, see sidebar) → line/section chip → theme toggle → user icon. */}
            <div className="header-extras-row header-extras-row--site">
            {!isNarrowMobile && plantContextReady && (availablePlants.length > 1 || plantName || activePlant) && (
              availablePlants.length > 1 ? (
                <HeaderDropdown
                  showLabel={false}
                  label="Unit / Plant"
                  value={activePlant || ''}
                    options={[
                      ...(isCrossUnitViewer ? [{ value: 'All Units', label: 'All Units' }] : []),
                      ...availablePlants.map(p => ({ value: p.plant_code, label: p.plant_name || p.plant_code }))
                    ]}
                  onChange={handlePlantChange}
                />
              ) : (
                <span className="header-role-badge">{plantName || activePlant}</span>
              )
            )}
            {filteredLines.length > 0 && (
              <HeaderDropdown
                showLabel={false}
                label="Section"
                value={activeLine || 'All'}
                options={lineOptions}
                displayLabel={activeLine && activeLine !== 'All' ? activeLine.toUpperCase() : 'All Sections'}
                onChange={handleLineChange}
              />
            )}
            </div>
            <div className="header-extras-row header-extras-row--user">
            {/* Color theme picker */}
            <div className="header-color-menu">
              <button
                ref={colorMenuRef}
                type="button"
                className={`header-color-trigger ${colorMenuOpen ? 'open' : ''}`}
                onClick={() => setColorMenuOpen((prev) => !prev)}
                title="My Theme"
                aria-label="My Theme"
              >
                <FaPalette />
              </button>
              <DropdownPortal open={colorMenuOpen} anchorRef={colorMenuRef} align="right" onClose={() => setColorMenuOpen(false)}>
                <div className="header-dropdown-menu header-color-dropdown">
                  <div className="header-color-dropdown-title">My Theme</div>
                  <div className="header-color-swatch-grid">
                    {colorOptions.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`header-color-swatch ${colorTheme === t.id ? 'active' : ''}`}
                        style={{ backgroundColor: t.color }}
                        title={t.label}
                        aria-label={t.label}
                        onClick={() => { setColorTheme(t.id); setColorMenuOpen(false); }}
                      />
                    ))}
                  </div>
                </div>
              </DropdownPortal>
            </div>
            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              className={`theme-switch ${theme === 'dark' ? 'dark' : ''}`}
            >
              <span className="theme-switch-thumb">
                {theme === 'light' ? <FaSun /> : <FaMoon />}
              </span>
            </button>
            {/* User icon menu — avatar on the right, Logout lives in the dropdown beneath it */}
            <div className="header-user-menu">
              <button
                ref={userMenuRef}
                type="button"
                className={`header-user-trigger ${userMenuOpen ? 'open' : ''}`}
                onClick={() => setUserMenuOpen((prev) => !prev)}
                title={user?.username}
                aria-label="User menu"
              >
                <span className="header-avatar">
                  {(user?.username || '').slice(0, 2).toUpperCase() || <FaUserCircle />}
                </span>
                <FaChevronDown className="header-user-trigger-caret" />
              </button>
              <DropdownPortal open={userMenuOpen} anchorRef={userMenuRef} align="right" onClose={() => setUserMenuOpen(false)}>
                <div className="header-dropdown-menu header-user-dropdown">
                  <div className="header-user-dropdown-info">
                    <div className="header-user-dropdown-name">{user?.username}</div>
                    {user?.role && <div className="header-user-dropdown-role">{user.role}</div>}
                  </div>
                  <button
                    type="button"
                    className="header-dropdown-item logout"
                    onClick={onLogout}
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              </DropdownPortal>
            </div>
            </div>
            </div>
          </div>
      </header>
        <main className="main-content">
          <Breadcrumbs />
          <div className="content-card">
            {pageLocked ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 6px', fontSize: 'var(--fs-xl)', fontWeight: 700 }}>{currentNavItem.label} is not part of your plan</h2>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Contact your administrator to have this module enabled.</p>
              </div>
            ) : <Outlet />}
          </div>
        </main>
        </div>
      </div>
      <button
        type="button"
        className="floating-back-btn"
        {...floatingBack.pointerHandlers}
        onClick={() => {
          if (floatingBack.consumeDraggedClick()) return;
          navigate(-1);
        }}
        title="Go back. Drag to move."
        aria-label="Go back; drag to move"
        style={{
          left: floatingBack.position.left,
          top: floatingBack.position.top,
          right: 'auto',
          bottom: 'auto',
          cursor: floatingBack.isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none'
        }}
      >
        <FaArrowLeft />
      </button>
      {isTabletOrMobile && (
        <TabletBottomNav
          items={filteredMenuItems}
          getVisibleChildren={getVisibleChildren}
          location={location}
          onNavigate={handleNavClick}
        />
      )}
      {(!moduleAccess || moduleAccess.ai_assistant !== false) && <AIChatWidget />}
      <StickyHScrollbar />
    </>
  );
};

export default Layout;
